import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  RefreshCw, Plus, Play, Trash2, Edit, Check, X, AlertTriangle,
  FileText, Zap, Settings, ChevronDown, ChevronUp
} from 'lucide-react';
import { HelpTooltip } from '@/components/help/HelpTooltip';

const OPERATORS = [
  { value: 'equals', label: 'Equals (=)' },
  { value: 'not_equals', label: 'Not Equals (!=)' },
  { value: 'greater_than', label: 'Greater Than (>)' },
  { value: 'less_than', label: 'Less Than (<)' },
  { value: 'greater_than_or_equals', label: 'Greater Than or Equals (>=)' },
  { value: 'less_than_or_equals', label: 'Less Than or Equals (<=)' },
  { value: 'is_true', label: 'Is True' },
  { value: 'is_false', label: 'Is False' },
];

const FIELDS = [
  { value: 'dialed', label: 'Dialed Calls', type: 'number' },
  { value: 'connected', label: 'Connected Calls', type: 'number' },
  { value: 'connect_rate', label: 'Connect Rate (%)', type: 'number' },
  { value: 'total_closure', label: 'Total Closures', type: 'number' },
  { value: 'total_visited', label: 'Total Visited', type: 'number' },
  { value: 'revenue_expected', label: 'Revenue Expected', type: 'number' },
  { value: 'tl_verified', label: 'TL Verified', type: 'boolean' },
  { value: 'mgr_verified', label: 'Manager Verified', type: 'boolean' },
  { value: 'pulse_status', label: 'Pulse Status', type: 'string' },
];

const CATEGORIES = [
  { value: 'performance', label: 'Performance', color: 'bg-blue-100 text-blue-800' },
  { value: 'reminder', label: 'Reminder', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'celebration', label: 'Celebration', color: 'bg-green-100 text-green-800' },
  { value: 'workflow', label: 'Workflow', color: 'bg-purple-100 text-purple-800' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' },
];

export default function RulesManagement() {
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rules');
  const [expandedRule, setExpandedRule] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [testData, setTestData] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testingRule, setTestingRule] = useState(null);

  // New rule form
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    category: 'performance',
    conditions: { all: [{ field: 'dialed', operator: 'greater_than', value: 0 }] },
    actions: [{ type: 'email', template: 'custom' }],
    priority: 5
  });

  const fetchData = useCallback(async () => {
    try {
      const [rulesRes, templatesRes] = await Promise.all([
        axiosInstance.get('/notification-rules'),
        axiosInstance.get('/notification-rules/templates')
      ]);
      setRules(rulesRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Failed to load rules');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleRule = async (ruleId, currentEnabled) => {
    try {
      await axiosInstance.post(`/notification-rules/${ruleId}/toggle`);
      toast.success(`Rule ${currentEnabled ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await axiosInstance.delete(`/notification-rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete rule');
    }
  };

  const handleTestRule = async (ruleId) => {
    if (!testData) {
      toast.error('Please enter test data');
      return;
    }
    try {
      const data = JSON.parse(testData);
      const response = await axiosInstance.post(`/notification-rules/${ruleId}/test`, data);
      setTestResult(response.data);
      setTestingRule(ruleId);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON in test data');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to test rule');
      }
    }
  };

  const handleCreateRule = async () => {
    try {
      await axiosInstance.post('/notification-rules', newRule);
      toast.success('Rule created successfully');
      setShowCreateModal(false);
      setNewRule({
        name: '',
        description: '',
        category: 'performance',
        conditions: { all: [{ field: 'dialed', operator: 'greater_than', value: 0 }] },
        actions: [{ type: 'email', template: 'custom' }],
        priority: 5
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rule');
    }
  };

  const addCondition = () => {
    setNewRule(prev => ({
      ...prev,
      conditions: {
        all: [...(prev.conditions.all || []), { field: 'dialed', operator: 'greater_than', value: 0 }]
      }
    }));
  };

  const updateCondition = (index, field, value) => {
    setNewRule(prev => ({
      ...prev,
      conditions: {
        all: prev.conditions.all.map((c, i) => i === index ? { ...c, [field]: value } : c)
      }
    }));
  };

  const removeCondition = (index) => {
    setNewRule(prev => ({
      ...prev,
      conditions: {
        all: prev.conditions.all.filter((_, i) => i !== index)
      }
    }));
  };

  const getCategoryBadge = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? (
      <Badge className={cat.color}>{cat.label}</Badge>
    ) : (
      <Badge>{category}</Badge>
    );
  };

  const formatConditions = (conditions) => {
    if (!conditions) return 'No conditions';
    const conds = conditions.all || conditions.any || [];
    const logic = conditions.all ? 'AND' : 'OR';
    return conds.map((c, i) => (
      <span key={i}>
        <code className="bg-slate-100 px-1 rounded">{c.field}</code>
        <span className="text-slate-500 mx-1">{c.operator.replace(/_/g, ' ')}</span>
        <code className="bg-slate-100 px-1 rounded">{JSON.stringify(c.value)}</code>
        {i < conds.length - 1 && <span className="text-blue-500 mx-2">{logic}</span>}
      </span>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            Notification Rules
            <HelpTooltip
              tip="Rules automatically send notifications when conditions are met"
              docId="rules-overview"
            />
          </h1>
          <p className="text-slate-500">Configure conditional notification logic</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateModal(true)} variant="default">
            <Plus size={16} className="mr-2" />
            Create Rule
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {['rules', 'templates', 'test'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
              activeTab === tab
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'templates' && (
              <HelpTooltip
                tip="Pre-built rules you can use as starting points"
                docId="rules-overview#templates"
                className="ml-1"
              />
            )}
            {tab === 'test' && (
              <HelpTooltip
                tip="Test your rule with sample data to see if it would trigger"
                docId="rules-overview#testing"
                className="ml-1"
              />
            )}
          </button>
        ))}
      </div>

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div className="space-y-4">
          {rules.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-slate-500">
                No rules configured. Create one or use a template.
              </CardContent>
            </Card>
          ) : (
            rules.map(rule => (
              <Card key={rule.rule_id} className={!rule.enabled ? 'opacity-60' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={rule.enabled !== false}
                        onCheckedChange={() => handleToggleRule(rule.rule_id, rule.enabled !== false)}
                      />
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {rule.name}
                          {rule.is_template && <Badge variant="outline" className="text-xs">Template</Badge>}
                        </CardTitle>
                        <CardDescription>{rule.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getCategoryBadge(rule.category)}
                      <HelpTooltip
                        tip="Categories help organize rules: Performance, Reminder, Critical, etc."
                        docId="rules-overview#creating"
                        className="ml-1"
                      />
                      <Badge variant="outline">Priority: {rule.priority}</Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedRule(expandedRule === rule.rule_id ? null : rule.rule_id)}
                      >
                        {expandedRule === rule.rule_id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {expandedRule === rule.rule_id && (
                  <CardContent>
                    <div className="space-y-4 pt-2 border-t">
                      <div>
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                          Conditions
                          <HelpTooltip
                            tip="ALL = every condition must match. ANY = just one must match."
                            docId="rules-overview#conditions"
                          />
                        </h4>
                        <div className="text-sm bg-slate-50 p-3 rounded">
                          {formatConditions(rule.conditions)}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Actions</h4>
                        <div className="flex gap-2">
                          {rule.actions?.map((action, i) => (
                            <Badge key={i} variant="outline">
                              {action.type}: {action.template}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setTestingRule(rule.rule_id);
                            setActiveTab('test');
                          }}
                        >
                          <Play size={14} className="mr-1" />
                          Test Rule
                        </Button>
                        {!rule.is_template && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteRule(rule.rule_id)}
                          >
                            <Trash2 size={14} className="mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(template => (
            <Card key={template.rule_id}>
              <CardHeader>
                <CardTitle className="text-lg">{template.name}</CardTitle>
                <CardDescription>{template.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    {getCategoryBadge(template.category)}
                    <Badge variant="outline">Priority: {template.priority}</Badge>
                  </div>
                  <div className="text-sm text-slate-600 mt-2">
                    <strong>Conditions:</strong>
                    <div className="bg-slate-50 p-2 rounded mt-1 text-xs">
                      {formatConditions(template.conditions)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Test Data</CardTitle>
              <CardDescription>Enter JSON data to test against rules</CardDescription>
            </CardHeader>
            <CardContent>
              <textarea
                className="w-full h-64 font-mono text-sm border rounded p-3"
                value={testData}
                onChange={(e) => setTestData(e.target.value)}
                placeholder={`{
  "dialed": 100,
  "connected": 45,
  "connect_rate": 45,
  "total_closure": 0,
  "total_visited": 10,
  "revenue_expected": 0,
  "tl_verified": true,
  "mgr_verified": false
}`}
              />
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => {
                    if (testingRule) {
                      handleTestRule(testingRule);
                    } else {
                      toast.error('Select a rule to test first');
                    }
                  }}
                  disabled={!testData}
                >
                  <Play size={16} className="mr-2" />
                  Test Rule
                </Button>
                <select
                  className="border rounded px-3 py-2"
                  value={testingRule || ''}
                  onChange={(e) => setTestingRule(e.target.value)}
                >
                  <option value="">Select a rule to test</option>
                  {rules.map(r => (
                    <option key={r.rule_id} value={r.rule_id}>{r.name}</option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Test Result</CardTitle>
              <CardDescription>See if rule would trigger</CardDescription>
            </CardHeader>
            <CardContent>
              {testResult ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${testResult.would_trigger ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                    <div className="flex items-center gap-2">
                      {testResult.would_trigger ? (
                        <>
                          <Check className="text-green-600" size={24} />
                          <span className="text-lg font-semibold text-green-800">Rule Would Trigger</span>
                        </>
                      ) : (
                        <>
                          <X className="text-slate-500" size={24} />
                          <span className="text-lg font-semibold text-slate-700">Rule Would Not Trigger</span>
                        </>
                      )}
                    </div>
                  </div>

                  {testResult.would_trigger && testResult.would_execute_actions?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Actions that would execute:</h4>
                      <div className="space-y-1">
                        {testResult.would_execute_actions.map((action, i) => (
                          <Badge key={i} className="mr-2">
                            {action.type}: {action.template}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="font-semibold mb-2">Rule tested:</h4>
                    <div className="bg-slate-50 p-3 rounded text-sm">
                      <p><strong>{testResult.rule?.name}</strong></p>
                      <p className="text-slate-600">{testResult.rule?.description}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-slate-500 py-8">
                  Enter test data and click "Test Rule" to see results
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Rule Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <CardHeader>
              <CardTitle>Create New Rule</CardTitle>
              <CardDescription>Define conditions and actions for your notification rule</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Rule Name</label>
                  <Input
                    value={newRule.name}
                    onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="My Custom Rule"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium flex items-center gap-1">
                    Category
                    <HelpTooltip
                      tip="Categories help organize rules: Performance, Reminder, Critical, etc."
                      docId="rules-overview#creating"
                    />
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={newRule.category}
                    onChange={(e) => setNewRule(prev => ({ ...prev, category: e.target.value }))}
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={newRule.description}
                  onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What this rule does..."
                />
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-1">
                  Priority (1 = highest)
                  <HelpTooltip
                    tip="Lower number = higher priority. Rules with priority 1 run before priority 10."
                    docId="rules-overview#creating"
                  />
                </label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={newRule.priority}
                  onChange={(e) => setNewRule(prev => ({ ...prev, priority: parseInt(e.target.value) || 5 }))}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium flex items-center gap-1">
                    Conditions (ALL must match)
                    <HelpTooltip
                      tip="ALL = every condition must match. ANY = just one must match."
                      docId="rules-overview#conditions"
                    />
                  </label>
                  <Button size="sm" variant="outline" onClick={addCondition}>
                    <Plus size={14} className="mr-1" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {newRule.conditions.all?.map((condition, index) => (
                    <div key={index} className="flex gap-2 items-center bg-slate-50 p-2 rounded">
                      <select
                        className="border rounded px-2 py-1 flex-1"
                        value={condition.field}
                        onChange={(e) => updateCondition(index, 'field', e.target.value)}
                      >
                        {FIELDS.map(f => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                      <select
                        className="border rounded px-2 py-1"
                        value={condition.operator}
                        onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                      >
                        {OPERATORS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {index === 0 && (
                        <HelpTooltip
                          tip="Compare field values: Greater Than (>), Less Than (<), Equals (=)"
                          docId="rules-overview#conditions"
                        />
                      )}
                      <Input
                        type="text"
                        className="w-24"
                        value={condition.value}
                        onChange={(e) => {
                          const val = e.target.value;
                          const numVal = parseFloat(val);
                          updateCondition(index, 'value', isNaN(numVal) ? val : numVal);
                        }}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500"
                        onClick={() => removeCondition(index)}
                        disabled={newRule.conditions.all?.length <= 1}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Actions</label>
                <div className="flex gap-2 mt-1">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newRule.actions.some(a => a.type === 'email')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewRule(prev => ({
                            ...prev,
                            actions: [...prev.actions.filter(a => a.type !== 'email'), { type: 'email', template: 'custom' }]
                          }));
                        } else {
                          setNewRule(prev => ({
                            ...prev,
                            actions: prev.actions.filter(a => a.type !== 'email')
                          }));
                        }
                      }}
                    />
                    Send Email
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newRule.actions.some(a => a.type === 'whatsapp')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewRule(prev => ({
                            ...prev,
                            actions: [...prev.actions.filter(a => a.type !== 'whatsapp'), { type: 'whatsapp', template: 'custom' }]
                          }));
                        } else {
                          setNewRule(prev => ({
                            ...prev,
                            actions: prev.actions.filter(a => a.type !== 'whatsapp')
                          }));
                        }
                      }}
                    />
                    Send WhatsApp
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateRule} disabled={!newRule.name || !newRule.description}>
                  Create Rule
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
